import React, { useState, useCallback } from 'react';
import { propOr, pathOr, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_SETTINGS_INFO } from '../apollo/queries';
import { CREATE_SHOP_SUBSCRIPTION, CANCEL_SHOP_SUBSCRIPTION } from '../apollo/mutation';
import Footer from '../components/Footer';
import { Page, Card, Layout, AppProvider, Frame, Toast } from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';
import Loader from 'react-loader-spinner';
import '@shopify/polaris/dist/styles.css';

const AccountPlan = (props) => {
  let { app, shopifyToken, shopOrigin, apiKey } = props;

  const redirect = Redirect.create(app);
  const [cancelShopSubscription] = useMutation(CANCEL_SHOP_SUBSCRIPTION);
  const [createShopSubscription] = useMutation(CREATE_SHOP_SUBSCRIPTION, {
    onCompleted(data) {
      redirect.dispatch(Redirect.Action.REMOTE, data.createShopSubscription.confirmationUrl);
    },
  });

  const [isLoading, setLoading] = useState(false);
  const [successToast, setSuccessToast] = useState(false);
  const toggleActive = useCallback(() => setSuccessToast((successToast) => !successToast), []);


  const { loading, data, refetch } = useQuery(GET_SETTINGS_INFO, {
    variables: { shopifyToken },
  });

  const shop = propOr({}, 'shop')(data);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const toastMarkup = successToast ? <Toast content='You successfully updated your settings!' onDismiss={toggleActive} /> : null;

  const createSubscription = (subscriptionType) => () => {
    const response = createShopSubscription({
      variables: {
        shopifyToken,
        subscriptionType,
      },
    });

    const errors = pathOr([], ['data', 'createShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const cancelSubscription = () => {
    setLoading(true);

    const response = cancelShopSubscription({
      variables: {
        shopifyToken,
        subscriptionId: currentSubscription.id,
      },
    });

    setTimeout(() => {
      refetch();
      redirect.dispatch(Redirect.Action.ADMIN_PATH, '/apps/shophire/job-list');
      setLoading(false);
      setSuccessToast(true);
    }, 500);

    const errors = pathOr([], ['data', 'cancelShopSubscription', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
  };

  const isPremiumPlan = currentSubscription.subscriptionType === 'Premium';
  const isGrowthPlan = currentSubscription.subscriptionType === 'Growth';
  const isBusinessPlan = currentSubscription.subscriptionType === 'Business';
  const isStarterPlan = isEmpty(currentSubscription);

  const modalUpgradePlanMarkup = (
    <div className='plans-cards-container'>
      <div className={`plan-card ${isStarterPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Starter</h1>
        <h2 className='plan-card-subtitle'>Free</h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> 3 Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Google for Jobs
          </li>
        </ul>
        {!isStarterPlan && (
          <button
            type='button'
            className='plan-card-button'
            onClick={() => {
              cancelSubscription();
              refetch();
            }}>
            {isStarterPlan ? 'Get started' : 'Downgrade plan'}
          </button>
        )}
      </div>
      <div className={`plan-card ${isGrowthPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Growth</h1>
        <h2 className='plan-card-subtitle'>
          $29<span style={{ fontSize: '10px' }}>month</span>
        </h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> 5 Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Webhooks
          </li>
        </ul>
        {!isGrowthPlan && (
          <button
            onClick={() => {
              createSubscription('Growth')();
            }}
            type='button'
            className='plan-card-button'>
            {isStarterPlan ? 'Upgrade plan' : 'Downgrade plan'}
          </button>
        )}
      </div>
      <div className={`plan-card ${isBusinessPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Business</h1>
        <h2 className='plan-card-subtitle'>
          $39<span style={{ fontSize: '10px' }}>month</span>
        </h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> 10 Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Webhooks
          </li>
        </ul>
        {!isBusinessPlan && (
          <button
            onClick={() => {
              createSubscription('Business')();
            }}
            type='button'
            className='plan-card-button'>
            {isStarterPlan || isGrowthPlan ? 'Upgrade plan' : 'Downgrade plan'}
          </button>
        )}
      </div>
      <div className={`plan-card ${isPremiumPlan ? 'is-active' : ''}`}>
        <h1 className='plan-card-title'>Premium</h1>
        <h2 className='plan-card-subtitle'>
          $49<span style={{ fontSize: '10px' }}>month</span>
        </h2>
        <ul className='plan-card-list'>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Unlimited Jobs
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Email Templates
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Custom Application Form
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Advanced Career Portal Features
          </li>
          <li style={{ marginBottom: '10px' }}>
            <span className='plan-card-check-item'>&#10003;</span> Webhooks
          </li>
        </ul>
        {!isPremiumPlan && (
          <button
            onClick={() => {
              createSubscription('Premium')();
            }}
            type='button'
            className='plan-card-button'>
            {isStarterPlan || isGrowthPlan || isBusinessPlan ? 'Upgrade plan' : 'Downgrade plan'}
          </button>
        )}
      </div>
    </div>
  );

  if (!loading) {
    return (
      <Frame>
        <TitleBar title='Account Plan' />
        <Page title='Account Plan'>
          <Layout>
            <div style={{ marginTop: '20px' }}>
              <Card sectioned>{modalUpgradePlanMarkup}</Card>
            </div>
            {toastMarkup}
          </Layout>
        </Page>
        <Footer link='https://help.shophire.co/article/8-settings' name='Account Plan' />
      </Frame>
    );
  } else {
    return (
      <AppProvider>
        <div
          style={{
            width: '100%',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
        </div>
      </AppProvider>
    );
  }
};

export default AccountPlan;
